import { Plus, Trash } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './Button';
import { Input } from './Input';

/**
 * Editor de una lista de textos (campos JSON `string[]` de la API).
 *
 * Es el hermano de un solo valor de `KeyValueFields`: filas con un `Input` y los botones de
 * agregar/eliminar. Se usa donde el backend espera `string[]` con ítems sueltos: `fotos`,
 * `reglas` y las `opciones` de una variante de `datosParaOrden`.
 *
 * Las filas se manejan por posición (no tienen `id` propio, a diferencia de las filas
 * clave/valor): el valor ES la fila. Las filas vacías se descartan al enviar
 * (`cleanStringList`), así que no hay que "limpiar" nada a mano y se puede quitar la última.
 *
 * Componente controlado: el estado lo tiene el formulario que lo usa.
 */

export interface StringListFieldsProps {
  values: string[];
  onChange: (values: string[]) => void;
  /** Nombre del ítem para los `aria-label` (ej. "Foto"). */
  itemLabel: string;
  placeholder?: string;
  addLabel?: string;
  disabled?: boolean;
  className?: string;
}

export function StringListFields({
  values,
  onChange,
  itemLabel,
  placeholder,
  addLabel = 'Agregar',
  disabled = false,
  className,
}: StringListFieldsProps) {
  function updateItem(index: number, value: string) {
    onChange(values.map((item, position) => (position === index ? value : item)));
  }

  function removeItem(index: number) {
    onChange(values.filter((_item, position) => position !== index));
  }

  return (
    <div className={cn(className)}>
      {values.length > 0 ? (
        <div className="space-y-2">
          {values.map((value, index) => (
            <div key={index} className="flex items-center gap-3">
              <Input
                value={value}
                placeholder={placeholder}
                disabled={disabled}
                aria-label={`${itemLabel} ${index + 1}`}
                onChange={(event) => updateItem(index, event.target.value)}
              />

              <Button
                variant="ghost"
                size="icon"
                disabled={disabled}
                aria-label={`Eliminar ${itemLabel.toLowerCase()} ${index + 1}`}
                onClick={() => removeItem(index)}
              >
                <Trash size={16} />
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      <Button
        variant="ghost"
        size="sm"
        className={values.length > 0 ? 'mt-2' : undefined}
        disabled={disabled}
        leftIcon={<Plus size={14} />}
        onClick={() => onChange([...values, ''])}
      >
        {addLabel}
      </Button>
    </div>
  );
}
